import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { formatarDinheiro } from '@/nucleo/dinheiro';
import { EmptyState } from '@/ui/EmptyState';
import { ChartContainer, type ChartConfig } from '@/ui/base/chart';

import type { LinhaDeDesempenho, LinhaPorDre, PontoDaTendencia, ValorMensal } from './contrato';

/**
 * Os gráficos do módulo.
 *
 * Existem porque o protótipo desenha estas seções como gráfico, não como
 * tabela. As séries e os valores foram lidos do próprio protótipo — no tooltip,
 * passando o mouse por categoria, ou na geometria da barra quando a seção não
 * tem tooltip. Não são estimativa nossa. `recharts` já é dependência do
 * repositório e já é usada em cinco lugares, então não há decisão de stack.
 *
 * Cor sempre por token semântico (regra 2 de interface): `hsl(var(--primary))`,
 * nunca hexadecimal cru.
 */

/**
 * Os três tipos TEG, na ordem e nas cores em que o protótipo os empilha. O tipo
 * Especial saiu por decisão do cliente; a matriz DRE × tipo TEG da Visão geral
 * ainda mostra a coluna, com o cabeçalho do protótipo (paridade.spec.ts).
 */
const TIPOS_TEG = [
  { chave: 'comum', rotulo: 'Comum', cor: 'hsl(var(--primary))' },
  { chave: 'acessivel', rotulo: 'Acessível', cor: 'hsl(var(--warning))' },
  { chave: 'creche', rotulo: 'Creche', cor: 'hsl(var(--destructive))' },
] as const;

const CONFIG_VALORES: ChartConfig = {
  valor: { label: 'Valor', color: 'hsl(var(--primary))' },
};

const CONFIG_TENDENCIA: ChartConfig = {
  osEmitidas: { label: 'OS Emitidas', color: 'hsl(var(--primary))' },
  osConcluidas: { label: 'OS Concluídas', color: 'hsl(var(--success))' },
};

const CONFIG_TIPOS: ChartConfig = Object.fromEntries(
  TIPOS_TEG.map((tipo) => [tipo.chave, { label: tipo.rotulo, color: tipo.cor }]),
);

const CONFIG_DRE: ChartConfig = {
  credenciados: { label: 'Credenciados', color: 'hsl(var(--primary))' },
};

/** AAAA-MM para "ago/26", sem depender de fuso: o mês já vem sem hora. */
function rotuloDoMes(mes: string): string {
  const [ano, numero] = mes.split('-');
  const nomes = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
  ];
  const indice = Number(numero) - 1;
  return nomes[indice] && ano ? `${nomes[indice]}/${ano.slice(2)}` : mes;
}

const EIXO = { stroke: 'hsl(var(--muted-foreground))', fontSize: 12 } as const;

export function GraficoDeValoresMensais({ dados }: { dados: readonly ValorMensal[] }) {
  if (dados.length === 0) {
    return (
      <EmptyState
        title="Sem valores no período"
        description="Nenhuma competência fechada até agora."
        variant="search"
      />
    );
  }

  const serie = dados.map((ponto) => ({ ...ponto, mes: rotuloDoMes(ponto.mes) }));

  return (
    <ChartContainer config={CONFIG_VALORES} className="h-64 w-full">
      <BarChart data={serie} accessibilityLayer>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="mes" tick={EIXO} />
        <YAxis tick={EIXO} width={80} tickFormatter={(valor: number) => formatarDinheiro(valor)} />
        <Tooltip formatter={(valor: number) => formatarDinheiro(valor)} />
        <Bar dataKey="valor" name="Valor" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ChartContainer>
  );
}

/**
 * "Veículos por DRE": barras empilhadas por tipo TEG, uma coluna por sigla de
 * diretoria. Mesma base da "Matriz DRE × Tipo TEG" — o protótipo desenha o
 * mesmo dado de duas maneiras, e as duas vêm.
 */
export function GraficoDeVeiculosPorDre({ dados }: { dados: readonly LinhaDeDesempenho[] }) {
  if (dados.length === 0) {
    return (
      <EmptyState
        title="Sem veículos no recorte"
        description="Nenhuma diretoria com veículo em operação."
        variant="search"
      />
    );
  }

  return (
    <ChartContainer config={CONFIG_TIPOS} className="h-64 w-full">
      <BarChart data={[...dados]} accessibilityLayer>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="codigo" tick={EIXO} />
        <YAxis tick={EIXO} width={40} />
        <Tooltip />
        <Legend />
        {TIPOS_TEG.map((tipo) => (
          <Bar
            key={tipo.chave}
            dataKey={tipo.chave}
            name={tipo.rotulo}
            stackId="tipoTeg"
            fill={tipo.cor}
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}

/** "Comparativo por DRE": credenciados por sigla de diretoria. */
export function GraficoComparativoPorDre({ dados }: { dados: readonly LinhaPorDre[] }) {
  if (dados.length === 0) {
    return (
      <EmptyState
        title="Sem diretoria no recorte"
        description="Nenhuma DRE com movimento no período escolhido."
        variant="search"
      />
    );
  }

  return (
    <ChartContainer config={CONFIG_DRE} className="h-64 w-full">
      <BarChart data={[...dados]} accessibilityLayer>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="codigo" tick={EIXO} />
        <YAxis tick={EIXO} width={40} />
        <Tooltip />
        <Bar
          dataKey="credenciados"
          name="Credenciados"
          fill="hsl(var(--primary))"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ChartContainer>
  );
}

export function GraficoDeTendencia({ dados }: { dados: readonly PontoDaTendencia[] }) {
  if (dados.length === 0) {
    return (
      <EmptyState
        title="Sem série histórica"
        description="Não há competências suficientes para desenhar a tendência."
        variant="search"
      />
    );
  }

  const serie = dados.map((ponto) => ({ ...ponto, mes: rotuloDoMes(ponto.mes) }));

  return (
    <ChartContainer config={CONFIG_TENDENCIA} className="h-64 w-full">
      <LineChart data={serie} accessibilityLayer>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis dataKey="mes" tick={EIXO} />
        <YAxis tick={EIXO} width={48} />
        <Tooltip />
        <Legend />
        <Line
          type="monotone"
          dataKey="osEmitidas"
          name="OS Emitidas"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
        />
        <Line
          type="monotone"
          dataKey="osConcluidas"
          name="OS Concluídas"
          stroke="hsl(var(--success))"
          strokeWidth={2}
        />
      </LineChart>
    </ChartContainer>
  );
}
